import type { CoverageAreaInput, CoveragePartyInput } from "./coverageLookup.js";
import type { CoverageNote } from "./coverageNotes.js";
import { circleRing, pointInCircle, pointInPolygon, ringsFromGeoJson } from "./pointInArea.js";
import type { LatLng } from "../maps/types.js";

export type CoverageCampaign = {
  id: string;
  name: string;
  platform: "google" | "facebook";
  spendUsd: number;
  startsOn: string;
  endsOn: string;
  active: boolean;
  kind: "circle" | "polygon";
  radiusKm: number | null;
  centerLat: number | null;
  centerLng: number | null;
  geojson: unknown;
};

export type ProblemSettings = {
  overlapCount: number;
  noteReviewDays: number;
  minSpendUsd?: number;
  quietDays?: number;
};

export type SalePoint = { lat: number; lng: number; soldAt: string };

export type CoverageProblem = {
  id: "spend_without_coverage" | "coverage_without_spend" | "spend_without_sales" | "stale_boundary" | "notes_past_review";
  title: string;
  detail: string;
};

const DAY_MS = 24 * 60 * 60 * 1000;

export function coverageProblems(args: {
  parties: CoveragePartyInput[];
  areas: CoverageAreaInput[];
  notes: CoverageNote[];
  campaigns: CoverageCampaign[];
  sales?: SalePoint[];
  settings: ProblemSettings;
  nowMs: number;
}): CoverageProblem[] {
  const problems: CoverageProblem[] = [];
  const running = args.campaigns.filter((campaign) => campaignIsRunning(campaign, args.nowMs));
  const quietDays = args.settings.quietDays ?? 30;
  const minSpend = args.settings.minSpendUsd ?? 0;
  const quietStart = args.nowMs - quietDays * DAY_MS;
  for (const campaign of running) {
    if (samples(campaign).some((point) => !insideFull(point, args.areas))) {
      problems.push({
        id: "spend_without_coverage",
        title: "Spend outside coverage",
        detail: `${campaign.name} spends $${campaign.spendUsd} where no full territory covers part of the area.`,
      });
    }
    if (!args.sales || campaign.spendUsd < minSpend) continue;
    const recent = args.sales.filter((sale) => {
      const sold = Date.parse(dateStart(sale.soldAt));
      return Number.isFinite(sold) && sold >= quietStart && sold <= args.nowMs;
    });
    if (recent.some((sale) => campaignContains(sale, campaign))) continue;
    problems.push({
      id: "spend_without_sales",
      title: "Spend with no sales",
      detail: `${campaign.name} spends $${campaign.spendUsd} and no sale landed in that area in the last ${quietDays} days.`,
    });
  }
  for (const party of args.parties) {
    if (!party.active || party.lat == null || party.lng == null) continue;
    const pin = { lat: party.lat, lng: party.lng };
    const overlap = new Set(
      args.areas.filter((area) => area.tier === "full" && contains(pin, area)).map((area) => area.partyId),
    );
    if (overlap.size < args.settings.overlapCount) continue;
    if (running.some((campaign) => campaignContains(pin, campaign))) continue;
    problems.push({
      id: "coverage_without_spend",
      title: "Coverage with no spend",
      detail: `${party.name} sits where ${overlap.size} providers overlap and no campaign is running.`,
    });
  }
  const staleAfter = args.settings.noteReviewDays * DAY_MS;
  for (const area of args.areas) {
    const confirmed = area.confirmedAt ? Date.parse(area.confirmedAt) : Number.NaN;
    if (Number.isFinite(confirmed) && args.nowMs - confirmed <= staleAfter) continue;
    const party = args.parties.find((row) => row.id === area.partyId);
    problems.push({
      id: "stale_boundary",
      title: "Stale boundary",
      detail: `${party?.name ?? "A plant"} has a ${area.tier} boundary that has not been reconfirmed.`,
    });
  }
  for (const note of args.notes) {
    if (note.invalidatedAt) continue;
    if (Date.parse(note.reviewAfter) > args.nowMs) continue;
    const party = args.parties.find((row) => row.id === note.partyId);
    problems.push({
      id: "notes_past_review",
      title: "Note past review",
      detail: `${party?.name ?? "A plant"}: ${note.body}`,
    });
  }
  return problems;
}

export function campaignIsRunning(campaign: CoverageCampaign, nowMs: number): boolean {
  if (!campaign.active) return false;
  const start = Date.parse(dateStart(campaign.startsOn));
  const end = Date.parse(dateEnd(campaign.endsOn));
  return Number.isFinite(start) && Number.isFinite(end) && start <= nowMs && nowMs <= end;
}

export function campaignContains(point: LatLng, campaign: CoverageCampaign): boolean {
  if (campaign.kind === "circle") {
    if (campaign.centerLat == null || campaign.centerLng == null || campaign.radiusKm == null) return false;
    return pointInCircle(point, { lat: campaign.centerLat, lng: campaign.centerLng }, campaign.radiusKm);
  }
  const rings = ringsFromGeoJson(campaign.geojson);
  return rings ? pointInPolygon(point, rings) : false;
}

function samples(campaign: CoverageCampaign): LatLng[] {
  if (campaign.kind === "circle") {
    if (campaign.centerLat == null || campaign.centerLng == null || campaign.radiusKm == null) return [];
    const center = { lat: campaign.centerLat, lng: campaign.centerLng };
    return [center, ...circleRing(center, campaign.radiusKm, 8).slice(0, 8)];
  }
  const rings = ringsFromGeoJson(campaign.geojson);
  return rings?.[0] ?? [];
}

function insideFull(point: LatLng, areas: CoverageAreaInput[]): boolean {
  return areas.some((area) => area.tier === "full" && contains(point, area));
}

function contains(point: LatLng, area: CoverageAreaInput): boolean {
  if (area.kind === "circle") {
    if (area.centerLat == null || area.centerLng == null || area.radiusKm == null) return false;
    return pointInCircle(point, { lat: area.centerLat, lng: area.centerLng }, area.radiusKm);
  }
  const rings = ringsFromGeoJson(area.geojson);
  return rings ? pointInPolygon(point, rings) : false;
}

function dateStart(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00.000Z` : value;
}

function dateEnd(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T23:59:59.999Z` : value;
}
